import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isVisible, validateAll, validateField } from '../../utils/formValidation'
import type { FormErrors, FormPreviewState, FormStatus, FormValues, FormVariant } from '../../types/form'

type State = {
  values: FormValues
  errors: FormErrors
  status: FormStatus
}

function initialState(variant: FormVariant, preview: FormPreviewState): State {
  switch (preview) {
    case 'filled':
      return { values: { ...variant.filledSample }, errors: {}, status: 'idle' }
    case 'errors':
      return {
        values: { ...variant.errorSample },
        errors: validateAll(variant.form.fields, variant.errorSample),
        status: 'idle',
      }
    case 'loading':
      return { values: { ...variant.filledSample }, errors: {}, status: 'loading' }
    case 'success':
      return { values: { ...variant.filledSample }, errors: {}, status: 'success' }
    default:
      return { values: {}, errors: {}, status: 'idle' }
  }
}

export function useFormCard(variant: FormVariant, preview: FormPreviewState) {
  const [state, setState] = useState<State>(() => initialState(variant, preview))
  const timer = useRef<number | undefined>(undefined)
  const navigate = useNavigate()
  const fields = variant.form.fields
  const idPrefix = `form-${variant.id}`

  useEffect(() => {
    window.clearTimeout(timer.current)
    setState(initialState(variant, preview))
  }, [variant, preview])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const visibleFields = fields.filter((f) => isVisible(f, state.values))

  const setValue = useCallback(
    (name: string, value: string | boolean) => {
      setState((s) => {
        const values = { ...s.values, [name]: value }
        const errors = { ...s.errors }
        // Re-check any field that currently shows an error, since dependent rules may change.
        Object.keys(errors).forEach((key) => {
          const field = fields.find((f) => f.name === key)
          const next = field && isVisible(field, values) ? validateField(field, values) : undefined
          if (next) errors[key] = next
          else delete errors[key]
        })
        return { ...s, values, errors }
      })
    },
    [fields],
  )

  const blur = useCallback(
    (name: string) => {
      setState((s) => {
        const field = fields.find((f) => f.name === name)
        const raw = s.values[name]
        if (!field || !raw) return s
        const error = validateField(field, s.values)
        const errors = { ...s.errors }
        if (error) errors[name] = error
        else delete errors[name]
        return { ...s, errors }
      })
    },
    [fields],
  )

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (state.status === 'loading') return
    const errors = validateAll(fields, state.values)
    const firstInvalid = fields.find((f) => errors[f.name])
    if (firstInvalid) {
      setState((s) => ({ ...s, errors }))
      window.requestAnimationFrame(() => document.getElementById(`${idPrefix}-${firstInvalid.name}`)?.focus())
      return
    }
    setState((s) => ({ ...s, errors: {}, status: 'loading' }))
    timer.current = window.setTimeout(() => {
      if (variant.redirectTo) navigate(variant.redirectTo)
      else setState((s) => ({ ...s, status: 'success' }))
    }, 1200)
  }

  return {
    ...state,
    idPrefix,
    visibleFields,
    setValue,
    blur,
    submit,
  }
}
